/**
 * ProductivityReport — daily recruiter productivity (Reports → Productivity).
 *
 * A per-recruiter view of recruiter-ATTRIBUTED activity over the range (calls,
 * talk time, texts, emails, leads worked, screens, DQs, callbacks booked, stage
 * advances, randomizations). Automated cadence/flow sends carry no user and are
 * excluded by the backend. Two views: a Leaderboard (per-recruiter cards) and a
 * Grid (recruiter × day matrix for one chosen metric). No chart library — the
 * grid is a heat-shaded table.
 */
import { useEffect, useMemo, useState } from 'react';
import { LayoutGrid, Trophy } from 'lucide-react';
import api from '../../../services/api';
import { ProductivityAnalytics } from '../../types';
import { getPref, setPref } from '../../lib';
import { FilterSelect, SegmentedControl } from '../filters';
import BarRow from './BarRow';
import { fmtDuration, SummaryStat, Stat } from './StatBlocks';
import { Z } from '../ui';

interface Props {
  from: string | null;
  to: string | null;
  studyId?: string | null;
}

const METRIC_LABELS: Record<string, string> = {
  calls_out: 'Calls',
  calls_connected: 'Connected',
  calls_in_handled: 'Inbound handled',
  talk_time_sec: 'Talk time',
  texts: 'Texts',
  emails: 'Emails',
  leads_worked: 'Leads worked',
  screens_started: 'Screens started',
  screens_completed: 'Screens done',
  dqs: 'DQs recorded',
  callbacks_booked: 'Callbacks booked',
  stage_advances: 'Stage advances',
  randomizations: 'Randomizations',
};

const isDuration = (metric: string) => metric === 'talk_time_sec';
const fmtMetric = (metric: string, n: number) => (isDuration(metric) ? fmtDuration(n) : (n || 0).toLocaleString());

/** 'YYYY-MM-DD' → 'Jun 1' (parsed as a local calendar day). */
function dayShort(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Primary-blue heat ramp for a grid cell, by value / column-max. */
function heatClass(value: number, max: number): string {
  if (!value || max <= 0) return '';
  const r = value / max;
  if (r > 0.8) return 'bg-primary-600 text-white';
  if (r > 0.6) return 'bg-primary-500 text-white';
  if (r > 0.4) return 'bg-primary-400 text-white dark:text-gray-900';
  if (r > 0.2) return 'bg-primary-200 text-primary-900 dark:bg-primary-800 dark:text-primary-100';
  return 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200';
}

export default function ProductivityReport({ from, to, studyId = null }: Props) {
  const [data, setData] = useState<ProductivityAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'leaderboard' | 'grid'>(
    () => (getPref('reports.productivity.view', 'leaderboard') === 'grid' ? 'grid' : 'leaderboard'),
  );
  const [gridMetric, setGridMetric] = useState<string>(() => getPref('reports.productivity.metric', 'calls_out'));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/crm/analytics/productivity', {
        params: { from: from || undefined, to: to || undefined, study_id: studyId || undefined },
      })
      .then((res) => {
        if (alive) {
          setData(res.data || null);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Failed to load productivity');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to, studyId]);

  // A persisted metric key that no longer exists (renamed/removed) would blank the
  // picker and zero the grid — clamp it to the live metric list after load.
  useEffect(() => {
    if (data && gridMetric && !data.metrics.includes(gridMetric)) {
      setGridMetric(data.metrics[0] || 'calls_out');
    }
  }, [data, gridMetric]);

  const setViewPref = (v: 'leaderboard' | 'grid') => {
    setView(v);
    setPref('reports.productivity.view', v);
  };
  const setMetricPref = (m: string) => {
    setGridMetric(m);
    setPref('reports.productivity.metric', m);
  };

  // Recruiters with any activity float to the leaderboard; the grid keeps them all.
  const activeRecruiters = useMemo(
    () => (data?.recruiters || []).filter((r) => Object.keys(r.totals).length > 0),
    [data],
  );
  const maxCalls = useMemo(
    () => Math.max(1, ...activeRecruiters.map((r) => r.totals.calls_out || 0)),
    [activeRecruiters],
  );
  const gridMax = useMemo(() => {
    if (!data) return 0;
    let m = 0;
    for (const r of data.recruiters) for (const day of data.days) m = Math.max(m, r.by_day[day]?.[gridMetric] || 0);
    return m;
  }, [data, gridMetric]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading productivity…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data || data.recruiters.length === 0)
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No recruiter activity in this range yet.</p>;

  const t = data.totals;

  return (
    <div className="space-y-4">
      {/* Network totals */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <SummaryStat label="Calls placed" value={(t.calls_out || 0).toLocaleString()} />
        <SummaryStat label="Talk time" value={fmtDuration(t.talk_time_sec || 0)} />
        <SummaryStat label="Texts" value={(t.texts || 0).toLocaleString()} />
        <SummaryStat label="Screens done" value={(t.screens_completed || 0).toLocaleString()} />
        <SummaryStat label="DQs recorded" value={(t.dqs || 0).toLocaleString()} />
        <SummaryStat label="Randomized" value={(t.randomizations || 0).toLocaleString()} highlight={(t.randomizations || 0) > 0} />
      </div>

      {/* View toggle + metric picker */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SegmentedControl
          label="Productivity view"
          value={view}
          onChange={(v) => setViewPref(v as 'leaderboard' | 'grid')}
          options={[
            { value: 'leaderboard', label: 'Leaderboard', icon: <Trophy className="h-4 w-4" /> },
            { value: 'grid', label: 'Daily grid', icon: <LayoutGrid className="h-4 w-4" /> },
          ]}
        />
        {view === 'grid' && (
          <FilterSelect value={gridMetric} onChange={setMetricPref} auto label="Grid metric">
            {data.metrics.map((m) => (
              <option key={m} value={m}>{METRIC_LABELS[m] || m}</option>
            ))}
          </FilterSelect>
        )}
      </div>

      {data.study_scoped && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Scoped to the selected study (activity attributed by each lead's current study).
        </p>
      )}

      {view === 'leaderboard' ? (
        <div className="space-y-2">
          {activeRecruiters.length === 0 ? (
            <p className="p-2 text-sm text-gray-500 dark:text-gray-400">No attributed activity in this range.</p>
          ) : (
            activeRecruiters.map((r) => (
              <div key={r.user_id} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{r.name || 'Unknown'}</h3>
                <div className="mt-2">
                  <BarRow
                    label="Calls"
                    count={r.totals.calls_out || 0}
                    max={maxCalls}
                    hint={r.totals.talk_time_sec ? fmtDuration(r.totals.talk_time_sec) : null}
                    color="bg-primary-500"
                  />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 px-2 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-4">
                  <Stat label="Texts" value={(r.totals.texts || 0).toLocaleString()} />
                  <Stat label="Emails" value={(r.totals.emails || 0).toLocaleString()} />
                  <Stat label="Leads worked" value={(r.totals.leads_worked || 0).toLocaleString()} />
                  <Stat label="Screens" value={(r.totals.screens_started || 0).toLocaleString()} />
                  <Stat label="Screens done" value={(r.totals.screens_completed || 0).toLocaleString()} />
                  <Stat label="DQs" value={(r.totals.dqs || 0).toLocaleString()} />
                  <Stat label="Callbacks" value={(r.totals.callbacks_booked || 0).toLocaleString()} />
                  <Stat label="Advances" value={(r.totals.stage_advances || 0).toLocaleString()} />
                  <Stat
                    label="Randomized"
                    value={(r.totals.randomizations || 0).toLocaleString()}
                    emphasize={(r.totals.randomizations || 0) > 0}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          {data.days.length === 0 ? (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No activity to chart in this range.</p>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className={`sticky left-0 ${Z.rail} bg-white px-3 py-2 text-left font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400`}>
                    {METRIC_LABELS[gridMetric] || gridMetric} · recruiter
                  </th>
                  {data.days.map((day) => (
                    <th key={day} className="px-2 py-2 text-center font-medium text-gray-400">{dayShort(day)}</th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Total</th>
                </tr>
              </thead>
              <tbody>
                {activeRecruiters.map((r) => (
                  <tr key={r.user_id} className="border-b border-gray-50 last:border-0 dark:border-gray-700/50">
                    <td className={`sticky left-0 ${Z.rail} max-w-[10rem] truncate bg-white px-3 py-1.5 font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200`}>
                      {r.name || 'Unknown'}
                    </td>
                    {data.days.map((day) => {
                      const v = r.by_day[day]?.[gridMetric] || 0;
                      return (
                        <td key={day} className="px-1 py-1 text-center">
                          {v > 0 ? (
                            <span className={`inline-block min-w-[1.75rem] rounded px-1.5 py-0.5 tabular-nums ${heatClass(v, gridMax)}`}>
                              {fmtMetric(gridMetric, v)}
                            </span>
                          ) : (
                            <span className="text-gray-300 dark:text-gray-600">·</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                      {fmtMetric(gridMetric, r.totals[gridMetric] || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <p className="px-1 text-[11px] leading-snug text-gray-400">
        Counts only recruiter-attributed activity (automated messages are excluded). Days are bucketed in {data.tz}.
        Randomizations reflect owned leads; CTMS/automation randomizations may not be recruiter-credited.
      </p>
    </div>
  );
}
